import { IconCancel, IconDuplicate, IconEdit, IconTrash } from 'assets/img';
import { DeleteButton, PrimaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { useNavigate } from 'react-router-dom';
import { ContextMenuItem } from 'redesign';
import { useUpdatePlanningPath } from 'ogp/components/views/planning/planning/hooks/use-get-path-helpers';
import { useAlert } from 'shared/components/alerts';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { useOgpArchiveShift } from '../query-hooks/ogp/use-ogp-archive-shift';

export type ShiftActionCallback = (action: 'edit' | 'duplicate', shiftId: string) => void;

type Props = {
  data?: ShiftDetail;
  setShiftAction: ShiftActionCallback;
  closeModal: () => void;
};

const useGetShiftDetailHeadActions = ({ data, closeModal, setShiftAction }: Props) => {
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();

  const navigate = useNavigate();
  const { alertSuccess, alertError } = useAlert();
  const updatePlanningPath = useUpdatePlanningPath();

  const { mutate, isLoading: isArchiving } = useOgpArchiveShift(data?.id, {
    onSuccess: () => {
      alertSuccess('De shift is succesvol verwijderd');
      navigate(updatePlanningPath({ shiftDetailId: null }));
      closeDialog();
      closeModal();
    },
    onError: (e) => alertError(e),
    onSettled: () => {
      enableDialog();
    },
  });

  const handleDelete = () => {
    openDialog(
      {
        title: 'Shift verwijderen',
        content:
          'Weet je zeker dat je de shift wilt verwijderen? Dit kan alleen als er geen geaccepteerde shiftclaims zijn.',
        secondaryButton: (props) => (
          <PrimaryButton startIcon={<IconCancel />} {...props} iconColorMode="fill">
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => <DeleteButton {...props} />,
      },
      () => {
        disableDialog();
        mutate();
      }
    );
  };

  const handleEdit = () => {
    if (!data) {
      return;
    }
    closeModal();
    setShiftAction('edit', data.id);
  };

  const handleDuplicate = () => {
    if (!data) {
      return;
    }
    closeModal();
    setShiftAction('duplicate', data.id);
  };

  const children = [
    <ContextMenuItem key="edit" onClick={handleEdit}>
      <IconEdit />
      Wijzig
    </ContextMenuItem>,
    <ContextMenuItem key="duplicate" onClick={handleDuplicate}>
      <IconDuplicate />
      Dupliceer
    </ContextMenuItem>,
    <ContextMenuItem key="delete" onClick={handleDelete}>
      <IconTrash />
      Verwijderen
    </ContextMenuItem>,
  ];

  return { children, isArchiving };
};

export { useGetShiftDetailHeadActions };
